import React, { useState, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTimes, 
  faUpload, 
  faCrown,
  faFilePdf,
  faFileImage,
  faFileWord,
  faFileExcel,
  faExclamationTriangle,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';
import { doc, collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { getFirestoreInstance } from '../../utils/firebaseHelper';
import { UserContext } from '../../context/UserContext';
import { TourContext } from '../../context/TourContext';
import styles from './AttachmentDetails.module.css';

const AttachmentDetails = ({ isOpen, onClose, eventId, dateId, eventType = 'tour', onAttachmentAdded }) => {
  const navigate = useNavigate();
  const { userData, authToken } = useContext(UserContext);
  const { selectedEvent } = useContext(TourContext);
  
  const [selectedFile, setSelectedFile] = useState(null);
  const [description, setDescription] = useState('');
  const [visibilityType, setVisibilityType] = useState('all');
  const [customVisibility, setCustomVisibility] = useState([]);
  const [showCustomSelection, setShowCustomSelection] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showAlert, setShowAlert] = useState(false);
  const [alertConfig, setAlertConfig] = useState({});
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Constants for upload configuration
  const ALLOWED_FILE_TYPES = {
    'application/pdf': { icon: faFilePdf, label: 'PDF' },
    'image/jpeg': { icon: faFileImage, label: 'Image' },
    'image/png': { icon: faFileImage, label: 'Image' },
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': { icon: faFileWord, label: 'Word' },
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': { icon: faFileExcel, label: 'Excel' }
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type
    if (!ALLOWED_FILE_TYPES[file.type]) {
      setAlertConfig({
        title: 'Invalid File Type',
        message: 'Please upload only PDF, Image (JPG/PNG), Word, or Excel files.',
        type: 'error'
      });
      setShowAlert(true);
      event.target.value = ''; // Clear input
      return;
    }

    // Max file size validation (for user experience purposes)
    const MAX_TOTAL_SIZE = 5 * 1024 * 1024; // 5MB absolute maximum
    if (file.size > MAX_TOTAL_SIZE) {
      setAlertConfig({
        title: 'File Too Large',
        message: `The selected file ${file.name} is ${(file.size / (1024 * 1024)).toFixed(2)}MB. Maximum allowed size is 5MB.`,
        type: 'error'
      });
      setShowAlert(true);
      event.target.value = ''; // Clear input
      return;
    }

    setSelectedFile(file);
  };

  const getEventMembers = () => {
    if (!selectedEvent) return [];
    
    const members = [];
    
    // Add creator (can be manager, artist, or organization)
    if (selectedEvent.createdBy) {
      members.push({
        userId: selectedEvent.createdBy.userId,
        fullName: selectedEvent.createdBy.fullName,
        userType: selectedEvent.createdBy.role || selectedEvent.createdBy.userType || 'Creator'
      });
    }
  
    // Try to get members from assignedUsers subcollection
    if (selectedEvent.assignedUsers && Array.isArray(selectedEvent.assignedUsers)) {
      selectedEvent.assignedUsers.forEach(user => {
        if (!members.some(m => m.userId === user.userId)) {
          members.push({
            userId: user.userId,
            fullName: user.fullName || user.userName,
            userType: user.role || user.userType
          });
        }
      });

     
    }

    return members;
  };

  const getMembersByType = (type) => {
    const members = getEventMembers();
    switch (type) {
      case 'artist':
        return members.filter(m => m.userType === 'Artist' || m.userType === 'artist').map(m => m.userId);
      case 'crew':
        return members.filter(m => m.userType === 'Crew' || m.userType === 'crew').map(m => m.userId);
      case 'me':
        return [userData.userId];
      case 'custom':
        return customVisibility;
      default:
        return []; // empty array means visible to all
    }
  };

  // This will use the processAndUploadMedia endpoint which works in Profile.js
  const uploadFile = async (file) => {
    try {
      setUploadProgress(10);
      console.log('Starting file upload using processAndUploadMedia');
      
      // Create FormData
      const formData = new FormData();
      
      // Use the 'media' field name to match what processAndUploadMedia expects
      formData.append('media', file);
      
      // Include type information - set as attachment for all files
      formData.append('type', 'attachment');
      
      // Include event and date info directly in the query params
      const queryParams = new URLSearchParams({
        context: 'attachment',  // Indicate this is an attachment
        eventId: eventId, 
        dateId: dateId,
        eventType: eventType // Add event type to the query
      }).toString();
      
      console.log('Uploading with params:', queryParams);
      
      // Make the request to processAndUploadMedia endpoint
      const response = await fetch(`/api/processAndUploadMedia?${queryParams}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`
        },
        body: formData
      });
      
      console.log('Response status:', response.status, response.statusText);
      
      // Check for error responses
      if (!response.ok) {
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to upload file');
        } else {
          const errorText = await response.text();
          console.error('Non-JSON error response:', errorText);
          throw new Error('Server returned an unexpected response format');
        }
      }
      
      const responseData = await response.json();
      console.log('Upload successful, response:', responseData);
      
      // Get file info from response
      let filePath, url, fileName, contentType;
      
      if (responseData.files && responseData.files.length > 0) {
        // Get the first file if multiple were uploaded
        const fileInfo = responseData.files[0];
        filePath = fileInfo.filePath;
        url = fileInfo.url;
        fileName = fileInfo.fileName || fileInfo.originalName;
        contentType = fileInfo.contentType;
      } else {
        // Direct file info
        filePath = responseData.filePath;
        url = responseData.url;
        fileName = responseData.fileName;
        contentType = responseData.contentType;
      }
      
      setUploadProgress(100);
      
      return {
        filePath,
        url,  // Direct URL from response
        fileName: fileName || file.name,
        contentType: contentType || file.type,
        size: file.size
      };
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  };

  const handleUpgrade = () => {
    navigate('/membership');
  };

  const getFileIcon = (fileType) => {
    return ALLOWED_FILE_TYPES[fileType]?.icon || faFilePdf;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedFile) return;
  
    setIsLoading(true);
    try {
      // Log file details for troubleshooting
      console.log(`Uploading ${selectedFile.name} (${selectedFile.size} bytes, type: ${selectedFile.type})`);
      
      // Upload the file
      const uploadResult = await uploadFile(selectedFile);
      
      // Check if we have a successful result
      if (!uploadResult || !uploadResult.url) {
        throw new Error('Upload failed or returned invalid result');
      }
      
      console.log('Upload succeeded with result:', uploadResult);
      
      // Get visibility members based on selected type
      const visibilityMembers = getMembersByType(visibilityType);
  
      // Create attachment object
      const newAttachment = {
        fileName: uploadResult.fileName || selectedFile.name,
        fileType: uploadResult.contentType || selectedFile.type,
        fileUrl: uploadResult.url,
        filePath: uploadResult.filePath, // Store the path for deletion later
        description,
        visibility: userData?.membershipTier === 'Elite' ? visibilityMembers : [],
        visibilityType: userData?.membershipTier === 'Elite' ? visibilityType : 'all',
        uploadedBy: userData.userName,
        uploaderId: userData.userId,
        uploadDate: new Date().toISOString(),
        size: selectedFile.size,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };
  
      // Add to Firestore using subcollection
      const db = getFirestoreInstance();
      const collectionName = eventType === 'tour' ? 'tours' : 'shows';
      
      // Create a reference to the attachment collection
      const attachmentsCollection = collection(db, collectionName, eventId, 'eventDates', dateId, 'attachments');
      
      // Add the attachment as a new document
      const docRef = await addDoc(attachmentsCollection, newAttachment);
      
      // Add the document ID to the attachment object for the callback
      newAttachment.id = docRef.id;
      
      // Call callback to inform parent component
      if (onAttachmentAdded && typeof onAttachmentAdded === 'function') {
        onAttachmentAdded(newAttachment);
      }
      
      // Close the modal
      onClose();
    } catch (error) {
      console.error('Error uploading attachment:', error);
      setAlertConfig({
        title: 'Error',
        message: `Failed to upload attachment: ${error.message}`,
        type: 'error'
      });
      setShowAlert(true);
    } finally {
      setIsLoading(false);
      setUploadProgress(0);
    }
  };

  const UpgradeModal = () => (
    <div className={styles.upgradeModalOverlay}>
      <div className={styles.upgradeModal}>
        <h3 className={styles.upgradeModalTitle}>Upgrade to Elite</h3>
        <p className={styles.upgradeModalMessage}>
          Upgrade to 'Elite' membership to set custom visibility for your attachments
        </p>
        <div className={styles.upgradeModalActions}>
          <button
            type="button"
            onClick={() => setShowUpgradeModal(false)}
            className={styles.cancelButton}
          >
            Close
          </button>
          <button
            type="button"
            onClick={handleUpgrade}
            className={styles.submitButton}
          >
            Upgrade
          </button>
        </div>
      </div>
    </div>
  );

  const AlertModal = ({ config, onClose }) => (
    <div className={styles.alertModalOverlay}>
      <div className={styles.alertModal}>
        <div className={styles.alertHeader}>
          <FontAwesomeIcon 
            icon={faExclamationTriangle} 
            className={`${styles.alertIcon} ${styles[config.type]}`}
          />
          <h3 className={styles.alertTitle}>{config.title}</h3>
        </div>
        <p className={styles.alertMessage}>{config.message}</p>
        <div className={styles.alertActions}>
          <button 
            className={`${styles.alertButton} ${styles[config.type]}`}
            onClick={onClose}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <button className={styles.closeButton} onClick={onClose}>
          <FontAwesomeIcon icon={faTimes} />
        </button>
        <h2 className={styles.modalTitle}>Add Attachment</h2>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.uploadSection}>
            {!selectedFile ? (
              <label className={styles.uploadArea}>
                <input
                  type="file"
                  onChange={handleFileSelect}
                  accept=".pdf,.docx,.xlsx,.jpg,.jpeg,.png"
                  className={styles.fileInput}
                />
                <FontAwesomeIcon icon={faUpload} className={styles.uploadIcon} />
                <div className={styles.uploadText}>
                  <p>Drop your file here or click to browse</p>
                  <span className={styles.uploadInfo}>
                    Maximum file size: 5MB<br />
                    Supported formats: PDF, Word, Excel, Images
                  </span>
                </div>
              </label>
            ) : (
              <div className={styles.selectedFileInfo}>
                <FontAwesomeIcon 
                  icon={getFileIcon(selectedFile.type)} 
                  className={styles.fileTypeIcon} 
                />
                <div className={styles.fileDetails}>
                  <p className={styles.fileName}>{selectedFile.name}</p>
                  <p className={styles.fileSize}>
                    {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                  </p>
                </div>
                <button 
                  type="button"
                  className={styles.removeFileButton}
                  onClick={() => setSelectedFile(null)}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            )}
          </div>

          {selectedFile && (
            <div className={styles.formGroup}>
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Add a description..."
                className={styles.textarea}
              />
            </div>
          )}

          <div className={styles.formGroup}>
            <div className={styles.visibilityHeader}>
              <label>Visibility</label>
              {userData?.membershipTier !== 'Elite' && (
                <button 
                  type="button"
                  className={styles.upgradeButton}
                  onClick={() => setShowUpgradeModal(true)}
                >
                  <FontAwesomeIcon icon={faCrown} />
                  Upgrade
                </button>
              )}
            </div>

            {userData?.membershipTier === 'Elite' ? (
              <div className={styles.visibilityControl}>
                <div className={styles.visibilityOptions}>
                  <label className={styles.visibilityOption}>
                    <input
                      type="radio"
                      name="visibility"
                      value="all"
                      checked={visibilityType === 'all'}
                      onChange={(e) => {
                        setVisibilityType(e.target.value);
                        setShowCustomSelection(false);
                      }}
                    />
                    <span>All Members</span>
                  </label>
                  <label className={styles.visibilityOption}>
                    <input
                      type="radio"
                      name="visibility"
                      value="artist"
                      checked={visibilityType === 'artist'}
                      onChange={(e) => {
                        setVisibilityType(e.target.value);
                        setShowCustomSelection(false);
                      }}
                    />
                    <span>Artists Only</span>
                  </label>
                  <label className={styles.visibilityOption}>
                    <input
                      type="radio"
                      name="visibility"
                      value="crew"
                      checked={visibilityType === 'crew'}
                      onChange={(e) => {
                        setVisibilityType(e.target.value);
                        setShowCustomSelection(false);
                      }}
                    />
                    <span>Crew Only</span>
                  </label>
                  <label className={styles.visibilityOption}>
                    <input
                      type="radio"
                      name="visibility"
                      value="me"
                      checked={visibilityType === 'me'}
                      onChange={(e) => {
                        setVisibilityType(e.target.value);
                        setShowCustomSelection(false);
                      }}
                    />
                    <span>Only Me</span>
                  </label>
                  <label className={styles.visibilityOption}>
                    <input
                      type="radio"
                      name="visibility"
                      value="custom"
                      checked={visibilityType === 'custom'}
                      onChange={(e) => {
                        setVisibilityType(e.target.value);
                        setShowCustomSelection(true);
                      }}
                    />
                    <span>Custom</span>
                  </label>
                </div>

                {showCustomSelection && (
                  <div className={styles.customVisibilitySelector}>
                    <p className={styles.customVisibilityTitle}>Select specific members:</p>
                    {getEventMembers().map(member => {
                      const isManager = member.userType === 'Manager' || member.userType === 'manager';
                      return (
                        <div 
                          key={member.userId} 
                          className={`${styles.customVisibilityOption} ${isManager ? styles.manager : ''}`}
                        >
                          <input
                            type="checkbox"
                            id={`custom-${member.userId}`}
                            checked={isManager || customVisibility.some(id => id === member.userId)}
                            onChange={(e) => {
                              if (!isManager) {
                                if (e.target.checked) {
                                  setCustomVisibility([...customVisibility, member.userId]);
                                } else {
                                  setCustomVisibility(customVisibility.filter(id => id !== member.userId));
                                }
                              }
                            }}
                            disabled={isManager}
                          />
                          <label htmlFor={`custom-${member.userId}`}>
                            {member.fullName} ({member.userType})
                          </label>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <div className={styles.visibilityInfo}>
                <span>Visible to all members</span>
                <span className={styles.eliteTag}>Elite feature</span>
              </div>
            )}
          </div>

          <div className={styles.formActions}>
            <button
              type="button"
              onClick={onClose}
              className={styles.cancelButton}
              disabled={isLoading}
            >
              Cancel
            </button>
            {selectedFile && (
              <button 
                type="submit" 
                className={styles.submitButton} 
                disabled={isLoading}
              >
                {isLoading ? 'Uploading...' : 'Upload'}
              </button>
            )}
          </div>
        </form>

        {showUpgradeModal && <UpgradeModal />}

        {showAlert && (
          <AlertModal 
            config={alertConfig}
            onClose={() => setShowAlert(false)}
          />
        )}

        {isLoading && (
          <div className={styles.loadingOverlay}>
            <div className={styles.progressContainer}>
              <div 
                className={styles.progressBar} 
                style={{ width: `${uploadProgress}%` }}
              />
              <span className={styles.progressText}>{uploadProgress}%</span>
            </div>
            <div className={styles.spinner}>
              <FontAwesomeIcon icon={faSpinner} spin />
            </div>
            <p className={styles.loadingText}>
              {uploadProgress < 100 ? 'Uploading file...' : 'Processing file...'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default AttachmentDetails;